<template>
  <div class="grid-x flex-grow">
    <div class="cell auto cell-block-y flex flex-col flex-grow">
      <div class="flex flex-col flex-grow">
        <div class="flex flex-row items-center justify-end p-4 border-b">
          <button
            type="button"
            class="flex flex-row items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded text-sm font-medium transition-colors"
            @click="openFiltersModal">
            <img src="@/assets/icons/filter.svg" alt="filter" class="w-4 h-4" />
            Фильтры
            <span v-if="regionId" class="ml-1 text-primary">(1)</span>
          </button>
        </div>
        <div class="flex-grow">
          <Loading :loading="waitPending">
            <Table
              :columns="columns"
              :rows="contractors"
              :row-style-class="rowStyleClass"
              @on-row-click="onRowClick"
              @on-sort-change="($event) => { orderBy = $event }"
            />
          </Loading>
        </div>
        <div class="flex flex-row items-end justify-center p-6">
          <Pagination
            :page="page"
            :page-count="pageCount"
            @change="onPageChange"
          />
        </div>
      </div>
    </div>
    <Modal
      id="contractors-filters"
      :hide-footer="true"
      :hide-header-close="true">
      <template v-slot:modal-title>
        <div class="flex items-center gap-2 justify-between">
          <span class="text-xl">Фильтры</span>
          <button class="size-[22px]" @click="closeFiltersModal">
            <img src="@/assets/icons/modal-header-close.svg" alt="" />
          </button>
        </div>
      </template>
      <div class="flex flex-col gap-6 p-6">
        <div class="flex flex-col gap-2">
          <span class="block text-sm font-medium">Регион работы</span>
          <Select
            :value="tempSelectedRegion"
            :options="regionFilterOptions"
            label="label"
            :reduce="(option) => option.value"
            placeholder="Выберите регион"
            :clearable="true"
            :searchable="true"
            @input="onTempRegionFilterChange"
          />
        </div>
        <div class="flex flex-row items-center justify-end gap-4">
          <button
            type="button"
            class="px-4 py-2 bg-gray-200 hover:bg-gray-300 rounded text-sm font-medium transition-colors"
            @click="resetTempFilters">
            Сбросить
          </button>
          <button
            type="button"
            class="px-4 py-2 bg-primary hover:bg-primary-dark text-white rounded text-sm font-medium transition-colors"
            @click="applyFilters">
            Применить
          </button>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script type="text/jsx">
import _ from 'lodash';
import { mapActions, mapState } from 'vuex';
import Table from '@/components/Table.vue';
import Pagination from '@/components/Pagination.vue';
import Loading from '@/components/Loading.vue';
import Select from '@/components/Select.vue';
import Modal from '@/components/Modal.vue';

export default {
  components: {
    Table,
    Pagination,
    Loading,
    Select,
    Modal,
  },
  data() {
    const routeQuery = this.$route.query || {};
    const sort = routeQuery.sort || null;
    const dir = routeQuery.dir || 'desc';
    const regionId = routeQuery.regionId ? Number(routeQuery.regionId) : null;

    return {
      limit: 100,
      page: Math.max(1, Number(routeQuery.page || 1) || 1),
      orderBy: sort ? [{ column: sort, descending: dir === 'desc' ? 1 : 0 }] : [],
      query: String(routeQuery.search || ''),
      regionId: Number.isFinite(regionId) && regionId > 0 ? regionId : null,
      tempRegionId: Number.isFinite(regionId) && regionId > 0 ? regionId : null,
      syncRouteLocked: false,
    };
  },
  computed: {
    ...mapState({
      payload: (state) => state.contractors.data,
      workingRegions: (state) => state.settings.workingRegions,
    }),
    columns() {
      return [
        {
          label: 'ID',
          field: 'id',
          tdClass: 'font-bold',
          sortable: true,
        },
        {
          label: 'ФИО',
          field: 'name',
          sortable: true,
        },
        {
          label: 'Тип исполнителя',
          field: 'businessType',
          cell: ({ row }) => (
            <div>
              { _.get(row, 'businessType.label') }
            </div>
          ),
          sortable: true,
        },
        {
          label: 'Телефон',
          field: 'phone',
          tdClass: 'align-middle',
          cell: ({ row }) => (
            <span
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();
              }}
            >
              {row.phone}
            </span>
          ),
          sortable: false,
        },
        {
          label: 'Регистрация',
          field: 'registrationDate',
          cell: ({ row }) => (
            <b>
              { this.$moment(row.registrationDate, 'YYYY-MM-DD').format('DD.MM.YYYY') }
            </b>
          ),
          sortable: true,
        },
        {
          label: 'Категории',
          field: 'serviceCategories',
          cell: ({ row }) => (
            <span title={ this.formatServiceCategories(row, false) }>
              { this.formatServiceCategories(row) }
            </span>
          ),
          sortable: false,
        },
        {
          label: 'Выполненных зак.',
          field: 'completedOrdersCount',
          tdClass: 'font-bold',
          sortable: true,
        },
        {
          label: 'Активных зак.',
          field: 'activeOrdersCount',
          tdClass: 'font-bold',
          sortable: true,
        },
        {
          label: 'Последний',
          field: 'lastOrderAssignedAt',
          cell: ({ row }) => {
            if (!row.lastOrderAssignedAt) return null;
            return (
              <b>
                { this.$moment(row.lastOrderAssignedAt, 'YYYY-MM-DD').format('DD.MM.YYYY') }
              </b>
            );
          },
          sortable: true,
        },
        {
          label: 'Баланс',
          field: 'balance',
          cell: ({ row }) => (
            <b>
              { this.$numeral(parseFloat(row.balance)).format('0,0 $') }
            </b>
          ),
          sortable: true,
        },
        {
          label: 'Регион работы',
          field: 'region',
          cell: ({ row }) => (
            <span>
              {[
                row.region?.country?.name,
                row.region?.name,
              ].filter(Boolean).join(', ')}
            </span>
          ),
        },
        {
          label: 'Город',
          field: 'city',
          cell: ({ row }) => (
            <span>{ row.city || '—' }</span>
          ),
          sortable: false,
        },
        {
          label: 'Рейтинг',
          field: 'rating',
          cell: ({ row }) => {
            if (row.rating !== null) {
              return (
                <div class="flex flex-row items-center space-x-1">
                  {/* eslint-disable-next-line global-require */}
                  <img className="mb-1" src={require('@/assets/icons/star.svg')} />
                  <b>{ this.$numeral(parseFloat(row.rating)).format('0.0') }</b>
                </div>
              );
            }
            return null;
          },
          sortable: true,
        },
      ];
    },
    offset() {
      return (this.page - 1) * this.limit;
    },
    contractors() {
      return _.get(this.payload, 'payload') || [];
    },
    total() {
      return _.get(this.payload, 'total') || null;
    },
    pageCount() {
      return Math.ceil(this.total / this.limit);
    },
    waitPending() {
      return this.$wait.is('fetch.contractors');
    },
    regionFilterOptions() {
      if (!this.workingRegions || !this.workingRegions.length) {
        return [];
      }
      return this.workingRegions.map((region) => ({
        value: region.id,
        label: `${region.country?.name || ''}, ${region.name || ''}`.replace(/^,\s*|,\s*$/g, ''),
      }));
    },
    selectedRegion() {
      if (!this.regionId) return null;
      return this.regionFilterOptions.find((option) => option.value === this.regionId) || null;
    },
    tempSelectedRegion() {
      if (!this.tempRegionId) return null;
      return this.regionFilterOptions.find((option) => option.value === this.tempRegionId) || null;
    },
  },
  methods: {
    ...mapActions({
      fetch: 'contractors/ACT_FETCH_CONTRACTORS',
      fetchWorkingRegions: 'settings/ACT_FETCH_WORKING_REGIONS',
    }),

    /**
     * Правила:
     *  - только disabledAt → розовый фон
     *  - только blockingReason → зачёркнуто + розовый фон
     *  - оба одновременно → зачёркнуто + розовый фон
     */
    rowStyleClass(row) {
      const classes = ['cursor-pointer'];

      const isBlockedByAdmin = !!row.blockingReason;
      const isDisabledByBalance = !!row.disabledAt;

      // Любой "отключён" → розовый фон
      if (isDisabledByBalance || isBlockedByAdmin) {
        classes.push('contractor-row-disabled');
      }

      // Бан админом → зачёркнутый красный
      if (isBlockedByAdmin) {
        classes.push('contractor-row-blocked');
      }

      return classes.join(' ');
    },

    normalizeServiceCategoryLabel(category) {
      if (category === null || typeof category === 'undefined') {
        return '';
      }

      if (typeof category === 'string' || typeof category === 'number') {
        return String(category);
      }

      return _.get(category, 'abbreviation')
        || _.get(category, 'code')
        || _.get(category, 'name')
        || _.get(category, 'label')
        || '';
    },
    formatServiceCategories(row, short = true) {
      const raw = _.get(row, 'serviceCategories')
        || _.get(row, 'serviceCategoryItems')
        || _.get(row, 'categories')
        || _.get(row, 'service_category_names')
        || _.get(row, 'serviceCategoryNames')
        || [];

      if (typeof raw === 'string') {
        return raw || '—';
      }

      const labels = (raw || [])
        .map((category) => this.normalizeServiceCategoryLabel(category))
        .filter(Boolean);

      if (!labels.length) {
        return '—';
      }

      if (!short) {
        return labels.join(', ');
      }

      const visible = labels.slice(0, 4).join(', ');
      return labels.length > 4 ? `${visible}, +${labels.length - 4}` : visible;
    },
    getDefaultOrderBy() {
      return [{ column: 'id', descending: 1 }];
    },
    getNormalizedOrderBy() {
      return _.isEqual(this.orderBy, []) ? this.getDefaultOrderBy() : this.orderBy;
    },
    buildListQuery() {
      const query = {};
      const search = String(this.query || '').trim();
      const sort = _.get(this.orderBy, '0.column');
      const descending = _.get(this.orderBy, '0.descending');

      if (this.page > 1) query.page = String(this.page);
      if (search) query.search = search;
      if (sort) {
        query.sort = String(sort);
        query.dir = descending ? 'desc' : 'asc';
      }
      if (this.regionId) query.regionId = String(this.regionId);

      return query;
    },
    normalizeRouteQuery(routeQuery = this.$route.query) {
      const normalized = {};
      ['page', 'search', 'sort', 'dir', 'regionId'].forEach((key) => {
        const value = routeQuery ? routeQuery[key] : undefined;
        if (value !== undefined && value !== null && String(value) !== '') {
          normalized[key] = String(value);
        }
      });
      return normalized;
    },
    syncRouteQuery() {
      if (this.syncRouteLocked) return;

      const nextListQuery = this.buildListQuery();
      const currentListQuery = this.normalizeRouteQuery();
      if (_.isEqual(currentListQuery, nextListQuery)) return;

      const nextQuery = { ...this.$route.query };
      ['page', 'search', 'sort', 'dir', 'regionId'].forEach((key) => delete nextQuery[key]);
      Object.assign(nextQuery, nextListQuery);

      this.$router.replace({
        name: this.$route.name,
        params: this.$route.params,
        query: nextQuery,
      }).catch(() => {});
    },
    applyRouteQuery(routeQuery = this.$route.query) {
      this.syncRouteLocked = true;

      this.page = Math.max(1, Number(routeQuery.page || 1) || 1);
      this.query = String(routeQuery.search || '');

      if (routeQuery.sort) {
        this.orderBy = [{
          column: routeQuery.sort,
          descending: routeQuery.dir === 'asc' ? 0 : 1,
        }];
      } else {
        this.orderBy = [];
      }

      const regionId = routeQuery.regionId ? Number(routeQuery.regionId) : null;
      this.regionId = Number.isFinite(regionId) && regionId > 0 ? regionId : null;
      this.tempRegionId = this.regionId;
      this.$eventBus.$emit('find.contractor.value', this.query);

      this.$nextTick(() => {
        this.syncRouteLocked = false;
      });
    },
    resetPageOrFetch() {
      if (this.page !== 1) {
        this.page = 1;
        return;
      }
      this.syncRouteQuery();
      this.fetchPayload();
    },
    applySearch(value) {
      const next = String(value || '').trim();
      if (next === this.query) {
        this.resetPageOrFetch();
        return;
      }
      this.query = next;
    },
    onPageChange(event) {
      this.page = event;
    },
    fetchPayload() {
      const params = {
        offset: this.offset,
        limit: this.limit,
        registered: 1,
        verified: 1,
        search: String(this.query || '').trim(),
        orderBy: this.getNormalizedOrderBy(),
      };
      if (this.regionId) {
        params.regionId = this.regionId;
      }
      this.fetch(params);
    },
    onTempRegionFilterChange(value) {
      this.tempRegionId = value || null;
    },
    commitRegionFilter(value) {
      const nextRegionId = value || null;
      const changed = this.regionId !== nextRegionId || this.page !== 1;

      this.tempRegionId = nextRegionId;
      if (!changed) return;

      this.syncRouteLocked = true;
      this.regionId = nextRegionId;
      this.page = 1;

      this.$nextTick(() => {
        this.syncRouteLocked = false;
        this.syncRouteQuery();
        this.fetchPayload();
      });
    },
    resetTempFilters() {
      this.commitRegionFilter(null);
      this.closeFiltersModal();
    },
    applyFilters() {
      this.commitRegionFilter(this.tempRegionId);
      this.closeFiltersModal();
    },
    openFiltersModal() {
      this.tempRegionId = this.regionId;
      this.$bvModal.show('contractors-filters');
    },
    closeFiltersModal() {
      this.$bvModal.hide('contractors-filters');
    },
    onRowClick({ row: event }) {
      this.$router.push({
        name: 'contractors.details',
        params: {
          id: event.id,
        },
      });
    },
  },
  watch: {
    page() {
      if (this.syncRouteLocked) return;
      this.syncRouteQuery();
      this.fetchPayload();
    },
    orderBy: {
      deep: true,
      handler() {
        if (this.syncRouteLocked) return;
        this.resetPageOrFetch();
      },
    },
    query() {
      if (this.syncRouteLocked) return;
      this.$eventBus.$emit('find.contractor.value', this.query);
      this.resetPageOrFetch();
    },
    regionId() {
      if (this.syncRouteLocked) return;
      this.resetPageOrFetch();
    },
    '$route.query': {
      deep: true,
      handler(value) {
        const routeState = this.normalizeRouteQuery(value);
        const currentState = this.buildListQuery();
        if (_.isEqual(routeState, currentState)) return;

        this.applyRouteQuery(value);
        this.$nextTick(() => this.fetchPayload());
      },
    },
  },
  mounted() {
    this.fetchWorkingRegions();
    this.$eventBus.$emit('find.contractor.value', this.query);
    this.fetchPayload();
    this.$eventBus.$on('find.contractor', this.applySearch);
  },
  beforeDestroy() {
    this.$eventBus.$off('find.contractor', this.applySearch);
  },
};
</script>
